import { DiContainer, InjectProperty } from '@jack-henry/frontend-utils/di';
import { LoggingService } from '@treasury/core/logging';
import { NavigationService } from '@treasury/core/navigation';
import { Action } from '@treasury/utils';
import { auditUpdates } from '@treasury/utils/lit-helpers';
import { LitElement, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { StyleMixin } from './mix-ins/style-mixin';
import { AppTheme, ThemeService } from './services/theme';

/**
 * Treasury web component base that providing common
 * functionality and abstractions across modern components.
 */
export abstract class TmBaseComponent extends StyleMixin(LitElement) {
    @InjectProperty()
    declare protected readonly themeService: ThemeService;

    @InjectProperty()
    declare protected readonly logger: LoggingService;

    @property({ type: String, reflect: true })
    protected theme = AppTheme.Light;

    private disconnectTasks: Action[] = [];

    private observer?: IntersectionObserver;

    /**
     * Using a verbose name to avoid name conflicts with downstream child classes.
     */
    @InjectProperty()
    declare private readonly baseComponentNavService: NavigationService;

    public connectedCallback() {
        super.connectedCallback();
        // Create an Intersection Observer to watch this element's visibility
        this.observer = new IntersectionObserver(([entry]) => {
            this.dispatchEvent(
                new CustomEvent('tm-component-visibility', {
                    detail: entry.isIntersecting,
                    bubbles: true,
                    composed: true,
                })
            );
        });

        const computedColor = getComputedStyle(document.documentElement).getPropertyValue(
            '--brand-primary'
        );
        const documentColor = document
            .querySelector('meta[name="theme-color"]')
            ?.getAttribute('content');

        if (computedColor !== documentColor) {
            document
                .querySelector('meta[name="theme-color"]')
                ?.setAttribute('content', computedColor);
        }

        // Start observing this element
        this.observer.observe(this);
        this.listenForTheme();
    }

    public disconnectedCallback() {
        super.disconnectedCallback();
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined;
        }

        this.disconnectTasks.forEach(task => task());
        this.disconnectTasks = [];
    }

    public async onBeforeLeave(
        location: unknown,
        commands: { prevent: () => { cancel: boolean } }
    ) {
        const canNavigate = await this.baseComponentNavService.executeInteractiveGuards();
        if (!canNavigate) {
            return commands.prevent();
        }

        return undefined;
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);
        this.auditUpdates(_changedProperties);
    }

    protected addCleanupTasks(...tasks: Action[]) {
        this.disconnectTasks.push(...tasks);
    }

    private async listenForTheme() {
        // ensure DI is ready before getting a ref to theme service
        await DiContainer.getInstance();
        this.themeService.theme$.subscribe(theme => {
            this.theme = theme;
        }, true);
    }

    private async auditUpdates(changedProps: PropertyValues) {
        await DiContainer.getInstance();

        auditUpdates(changedProps, this, (...args) => {
            this.logger.logDebug(...args);
        });
    }
}
